import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { getAccessToken, getActiveCompanyId, buildQuery, unwrapList, unwrapRecordOrObject, unwrapError, getTransportError } from './apiUtils';

export interface DashboardSummary {
  sales24h: number;
  salesPrev24h: number;
  tickets24h: number;
  booksInStock: number;
  titlesInStock: number;
  pendingOrders: number;
  pendingOrdersNew24h: number;
}

const num = (v: any): number => (isNaN(Number(v)) ? 0 : Number(v));

/** Cifras del panel desde dashboard_summary, para la sociedad activa. */
export const getDashboardSummary = async (): Promise<DashboardSummary> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`/dashboard_summary${buildQuery({ access_token: token, company_id: getActiveCompanyId() })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);

  const raw = unwrapRecordOrObject(res);
  if (!raw || typeof raw !== 'object' || raw.sales_24h === undefined) {
    throw new Error(unwrapError(res, 'No se pudieron cargar las cifras del panel.'));
  }
  return {
    sales24h:            num(raw.sales_24h),
    salesPrev24h:        num(raw.sales_prev_24h),
    tickets24h:          num(raw.tickets_24h),
    booksInStock:        num(raw.books_in_stock),
    titlesInStock:       num(raw.titles_in_stock),
    pendingOrders:       num(raw.pending_orders),
    pendingOrdersNew24h: num(raw.pending_orders_new_24h)
  };
};

export interface SalesFlowPoint {
  name: string;
  sales: number;
  tickets: number;
}

/** Ventas netas por hora de las últimas 24 h (dashboard_sales_flow), con las horas sin ventas a 0. */
export const getSalesFlow = async (): Promise<SalesFlowPoint[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`/dashboard_sales_flow${buildQuery({ access_token: token, company_id: getActiveCompanyId() })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);

  return unwrapList(res).map((raw: any): SalesFlowPoint => ({
    name:    String(raw.name ?? ''),
    sales:   num(raw.sales),
    tickets: num(raw.tickets)
  }));
};

export interface ActivityEntry {
  kind: 'ticket' | 'purchase_order';
  reference: string;
  /** Número de la factura del ticket, si la tiene (solo kind 'ticket'). */
  invoiceNumber: string;
  party: string;
  items: number;
  status: string;
  /** Devolución: su factura es un abono (solo kind 'ticket'). */
  isReturn: boolean;
  total: number;
  happenedAt: string;
}

const STATUS_LABELS: Record<string, string> = {
  sale:               'Venta',
  return:             'Devolución',
  draft:              'Borrador',
  pending_approval:   'Pendiente de aprobación',
  approved:           'Aprobado',
  partially_received: 'Recibido parcial',
  received:           'Recibido',
  cancelled:          'Cancelado',
  rejected:           'Rechazado'
};

/** Últimos movimientos (tickets del TPV y pedidos de compra) desde dashboard_recent_activity. */
export const getRecentActivity = async (): Promise<ActivityEntry[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`/dashboard_recent_activity${buildQuery({ access_token: token, company_id: getActiveCompanyId() })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);

  return unwrapList(res).map((raw: any): ActivityEntry => ({
    kind:       raw.kind === 'purchase_order' ? 'purchase_order' : 'ticket',
    reference:  String(raw.reference ?? ''),
    invoiceNumber: raw.invoice_number ? String(raw.invoice_number) : '',
    isReturn:   raw.status === 'return',
    party:      raw.party || '',
    items:      num(raw.items),
    status:     STATUS_LABELS[raw.status] || String(raw.status ?? ''),
    total:      num(raw.total),
    happenedAt: String(raw.happened_at ?? '')
  }));
};

export interface CriticalBook {
  id: string;
  title: string;
  author: string;
  stock: number;
  onOrder: number;
  price: number;
}

/** Libros con stock bajo (stock_levels_list): los de menos existencias primero, más el total que cumple el umbral. */
export const getCriticalInventory = async (
  options: { maxStock?: number; limit?: number } = {}
): Promise<{ books: CriticalBook[]; total: number }> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`/stock_levels_list${buildQuery({
    access_token: token,
    max_stock:    options.maxStock === undefined ? 5 : options.maxStock,
    page:         1,
    page_size:    options.limit || 5
  })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);

  const rows = unwrapList(res);
  return {
    books: rows.map((raw: any): CriticalBook => ({
      id:      String(raw.id),
      title:   raw.title || '',
      author:  raw.author || '',
      stock:   num(raw.stock),
      onOrder: num(raw.on_order),
      price:   num(raw.price)
    })),
    total: rows.length > 0 ? num(rows[0].total_count) : 0
  };
};
